import { useState } from 'react';
import { SectionTitle } from '@/components/Luxury';
import { formatCurrency, formatPercent } from '../../utils/formatters';
import { MetricLabel } from './MetricLabel';
import type { OfferSolution } from '../../types/calculations';
import type { ScenarioId } from './ScenarioSelector';

interface CumulativeReturnPanelProps {
  offerSolutions: OfferSolution[];
  rentGrowthLabel?: string;
  /** When provided, syncs this panel with the global scenario selector (Phase 180) */
  selectedScenario?: ScenarioId;
  /** Callback when user clicks a tab inside this panel — propagates to global selector */
  onScenarioChange?: (id: ScenarioId) => void;
}

const SCENARIO_TABS: { id: ScenarioId; label: string }[] = [
  { id: 'std-clean', label: 'Std + Clean' },
  { id: 'obbb-clean', label: 'OBBB + Clean' },
  { id: 'std-1031', label: 'Std + 1031' },
  { id: 'obbb-1031', label: 'OBBB + 1031' },
];

function StatRow({ label, value, accent }: { label: React.ReactNode; value: string; accent?: boolean }) {
  return (
    <div className="flex justify-between py-1">
      <span className="text-stone-400 text-sm">{label}</span>
      <span className={`font-mono tabular-nums text-sm ${accent ? 'text-gold font-bold' : 'text-ivory'}`}>{value}</span>
    </div>
  );
}

export function CumulativeReturnPanel({
  offerSolutions,
  rentGrowthLabel,
  selectedScenario: externalScenario,
  onScenarioChange,
}: CumulativeReturnPanelProps) {
  const [internalTab, setInternalTab] = useState<ScenarioId>('obbb-1031');

  // Use external scenario when provided (bidirectional sync), fall back to internal state
  const activeTab = externalScenario ?? internalTab;

  const handleTabChange = (id: ScenarioId) => {
    setInternalTab(id);
    onScenarioChange?.(id);
  };

  const solution = offerSolutions.find(s => s.scenario === activeTab);

  if (!solution) {
    return (
      <div className="bg-charcoal/40 border border-slate-dark rounded-lg p-4">
        <SectionTitle>Cumulative Return Analysis</SectionTitle>
        <div className="flex gap-1 mb-4 bg-obsidian rounded-md p-1">
          {SCENARIO_TABS.map(tab => (
            <button
              key={tab.id}
              onClick={() => handleTabChange(tab.id)}
              className={`flex-1 px-3 py-1.5 rounded text-sm font-medium transition-colors ${
                activeTab === tab.id
                  ? 'bg-gold/20 text-gold border border-gold/40'
                  : 'text-stone-400 hover:text-ivory hover:bg-charcoal/60'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <p className="text-stone-400 text-sm text-center py-4">No offer solution for this scenario</p>
      </div>
    );
  }

  const equityRequired = solution.equity_required ?? 0;
  const rows = solution.year_by_year ?? [];

  return (
    <div className="bg-charcoal/40 border border-slate-dark rounded-lg p-4">
      <SectionTitle>Cumulative Return Analysis{rentGrowthLabel ? ` — ${rentGrowthLabel}` : ''}</SectionTitle>

      {/* Scenario Tabs */}
      <div className="flex gap-1 mb-4 bg-obsidian rounded-md p-1">
        {SCENARIO_TABS.map(tab => {
          const hasSolution = offerSolutions.some(s => s.scenario === tab.id);
          return (
            <button
              key={tab.id}
              onClick={() => handleTabChange(tab.id)}
              disabled={!hasSolution}
              className={`flex-1 px-3 py-1.5 rounded text-sm font-medium transition-colors ${
                activeTab === tab.id
                  ? 'bg-gold/20 text-gold border border-gold/40'
                  : hasSolution
                    ? 'text-stone-400 hover:text-ivory hover:bg-charcoal/60'
                    : 'text-stone-400 cursor-not-allowed'
              }`}
            >
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* 3-Column Snapshot */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        {/* Capital Structure */}
        <div className="bg-obsidian/50 rounded-lg p-3">
          <div className="text-sm uppercase tracking-wider text-stone-400 mb-2">Capital Structure</div>
          <StatRow label="Offer Price" value={formatCurrency(solution.offer_price)} />
          <StatRow label="Equity Required" value={formatCurrency(equityRequired)} />
          <StatRow label={<MetricLabel metric="ltv">LTV</MetricLabel>} value={solution.ltv != null ? formatPercent(solution.ltv, 1) : '—'} />
          <StatRow label={<MetricLabel metric="dscr">DSCR</MetricLabel>} value={solution.dscr != null ? `${solution.dscr.toFixed(2)}x` : '—'} />
        </div>

        {/* Returns */}
        <div className="bg-obsidian/50 rounded-lg p-3">
          <div className="text-sm uppercase tracking-wider text-stone-400 mb-2">Returns</div>
          {/* Year 1 CoC — pre-tax and after-tax */}
          {rows.length > 0 && equityRequired > 0 && (() => {
            const yr1 = rows[0];
            const yr1PreTaxCF = yr1?.pre_tax_cf ?? (yr1 as any)?.cash_flow ?? 0;
            const yr1TaxBenefit = yr1?.tax_benefit ?? 0;
            const yr1AfterTaxCF = yr1PreTaxCF + yr1TaxBenefit;
            const preTaxCoC = yr1PreTaxCF / equityRequired;
            const afterTaxCoC = yr1AfterTaxCF / equityRequired;
            return (
              <>
                <StatRow label="Yr 1 Pre-Tax CoC" value={formatPercent(preTaxCoC, 1)} />
                <StatRow label="Yr 1 After-Tax CoC" value={formatPercent(afterTaxCoC, 1)} accent />
              </>
            );
          })()}
          <StatRow label={<MetricLabel metric="irr">5yr IRR</MetricLabel>} value={solution.irr_5yr != null ? formatPercent(solution.irr_5yr, 1) : '—'} accent />
          <StatRow label={<MetricLabel metric="irr">10yr IRR</MetricLabel>} value={solution.irr_10yr != null ? formatPercent(solution.irr_10yr, 1) : '—'} accent />
          <StatRow label={<MetricLabel metric="equity_multiple">5yr Multiple</MetricLabel>} value={solution.equity_multiple_5yr != null ? `${solution.equity_multiple_5yr.toFixed(2)}x` : '—'} />
          <StatRow label={<MetricLabel metric="equity_multiple">10yr Multiple</MetricLabel>} value={solution.equity_multiple_10yr != null ? `${solution.equity_multiple_10yr.toFixed(2)}x` : '—'} />
        </div>

        {/* Exit & Taxes */}
        <div className="bg-obsidian/50 rounded-lg p-3">
          <div className="text-sm uppercase tracking-wider text-stone-400 mb-2">Exit & Taxes</div>
          <StatRow label="Y1 Tax Benefit" value={formatCurrency(solution.year1_tax_benefit ?? 0)} accent />
          <StatRow label="5yr Tax Savings" value={formatCurrency(solution.total_tax_savings_5yr ?? 0)} />
          {solution.exit_strategy === 'clean' ? (
            <StatRow label="Exit Taxes (10yr)" value={solution.exit_taxes != null ? formatCurrency(solution.exit_taxes) : '—'} />
          ) : (
            <StatRow label="Deferred Taxes" value={solution.deferred_taxes != null ? formatCurrency(solution.deferred_taxes) : '—'} />
          )}
          <StatRow
            label="Bonus Depr"
            value={solution.bonus_depreciation ? 'OBBB 100%' : 'Standard Only'}
            accent={solution.bonus_depreciation}
          />
        </div>
      </div>

      {/* Return Breakdown — how the equity multiple is computed */}
      {rows.length >= 5 && (() => {
        const y5 = rows[4]; // year 5
        const y10 = rows.length >= 10 ? rows[9] : null; // year 10
        const y5Exit = y5?.exit_after_tax_net_proceeds ?? 0;
        const y10Exit = y10?.exit_after_tax_net_proceeds ?? 0;
        const y5Cum = y5?.cumulative ?? 0;
        const y10Cum = y10?.cumulative ?? 0;
        const y5Total = y5Cum + y5Exit;
        const y10Total = y10Cum + (y10Exit || 0);
        return (
          <div className="bg-obsidian/30 border border-slate-dark/50 rounded-lg p-3 mb-3">
            <div className="text-sm uppercase tracking-wider text-stone-400 mb-2">How Equity Multiple Is Computed</div>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <div className="text-stone-400 mb-1">5-Year Exit</div>
                <div className="flex justify-between"><span className="text-stone-400">Cumul. Cash Flow</span><span className="font-mono tabular-nums text-ivory">{formatCurrency(y5Cum)}</span></div>
                <div className="flex justify-between"><span className="text-stone-400">+ Exit Proceeds</span><span className="font-mono tabular-nums text-emerald">{formatCurrency(y5Exit)}</span></div>
                <div className="flex justify-between border-t border-slate-dark/50 mt-1 pt-1"><span className="text-stone-400">Total Return</span><span className="font-mono tabular-nums text-gold">{formatCurrency(y5Total)}</span></div>
                <div className="flex justify-between"><span className="text-stone-400">÷ Equity</span><span className="font-mono tabular-nums text-stone-400">{formatCurrency(equityRequired)}</span></div>
                <div className="flex justify-between mt-1"><span className="text-gold font-medium">= Multiple</span><span className="font-mono tabular-nums text-gold font-bold">{equityRequired > 0 ? (y5Total / equityRequired).toFixed(2) : '—'}x</span></div>
              </div>
              {y10 && (
                <div>
                  <div className="text-stone-400 mb-1">10-Year Exit</div>
                  <div className="flex justify-between"><span className="text-stone-400">Cumul. Cash Flow</span><span className="font-mono tabular-nums text-ivory">{formatCurrency(y10Cum)}</span></div>
                  <div className="flex justify-between"><span className="text-stone-400">+ Exit Proceeds</span><span className="font-mono tabular-nums text-emerald">{formatCurrency(y10Exit)}</span></div>
                  <div className="flex justify-between border-t border-slate-dark/50 mt-1 pt-1"><span className="text-stone-400">Total Return</span><span className="font-mono tabular-nums text-gold">{formatCurrency(y10Total)}</span></div>
                  <div className="flex justify-between"><span className="text-stone-400">÷ Equity</span><span className="font-mono tabular-nums text-stone-400">{formatCurrency(equityRequired)}</span></div>
                  <div className="flex justify-between mt-1"><span className="text-gold font-medium">= Multiple</span><span className="font-mono tabular-nums text-gold font-bold">{equityRequired > 0 ? (y10Total / equityRequired).toFixed(2) : '—'}x</span></div>
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {/* Year-by-Year Table */}
      {rows.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm tabular-nums">
            <thead>
              <tr className="border-b border-slate-dark text-stone-400">
                <th className="text-center py-2 px-2">Yr</th>
                <th className="text-right py-2 px-2">NOI</th>
                <th className="text-right py-2 px-2">Debt Svc</th>
                <th className="text-right py-2 px-2">Pre-Tax CF</th>
                <th className="text-right py-2 px-2">Tax Benefit</th>
                <th className="text-right py-2 px-2">After-Tax CF</th>
                <th className="text-right py-2 px-2">Cumulative</th>
                <th className="text-right py-2 px-2">Multiple</th>
              </tr>
            </thead>
            <tbody>
              {/* Year 0 row */}
              <tr className="text-stone-400 italic border-b border-slate-dark/50">
                <td className="text-center py-1.5 px-2">0</td>
                <td className="text-right py-1.5 px-2">—</td>
                <td className="text-right py-1.5 px-2">—</td>
                <td className="text-right py-1.5 px-2 text-crimson font-mono">
                  {formatCurrency(-equityRequired)}
                </td>
                <td className="text-right py-1.5 px-2">—</td>
                <td className="text-right py-1.5 px-2 text-crimson font-mono">
                  {formatCurrency(-equityRequired)}
                </td>
                <td className="text-right py-1.5 px-2 text-crimson font-mono">
                  {formatCurrency(-equityRequired)}
                </td>
                <td className="text-right py-1.5 px-2">—</td>
              </tr>
              {rows.map(row => (
                <tr
                  key={row.year}
                  className={`border-b border-slate-dark/30 ${
                    row.year === 5 ? 'bg-amber-900/10' : ''
                  }`}
                >
                  <td className={`text-center py-1.5 px-2 ${row.year === 5 ? 'text-amber-400 font-bold' : 'text-stone-400'}`}>
                    {row.year}
                  </td>
                  <td className="text-right py-1.5 px-2 font-mono text-ivory">{formatCurrency(row.noi)}</td>
                  <td className="text-right py-1.5 px-2 font-mono text-ivory">{formatCurrency(row.debt_service)}</td>
                  <td className={`text-right py-1.5 px-2 font-mono ${row.pre_tax_cf >= 0 ? 'text-emerald' : 'text-crimson'}`}>
                    {formatCurrency(row.pre_tax_cf)}
                  </td>
                  <td className="text-right py-1.5 px-2 font-mono text-emerald">{formatCurrency(row.tax_benefit)}</td>
                  <td className="text-right py-1.5 px-2 font-mono text-emerald">{formatCurrency(row.after_tax_cf)}</td>
                  <td className={`text-right py-1.5 px-2 font-mono ${row.cumulative >= 0 ? 'text-emerald' : 'text-crimson'}`}>
                    {formatCurrency(row.cumulative)}
                  </td>
                  <td className="text-right py-1.5 px-2 font-mono text-stone-400">{row.equity_multiple.toFixed(2)}x</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
